import { useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Newspaper } from "lucide-react";
import { Input, Area, ImageInput, VideoInput, GalleryField, Select, Toggle } from "./fields";
import { FicheOverlay, ItemCard, CardGrid } from "./Fiche";

const uid = () => `news-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

// Simple Festival news manager: cover + date + title + text + one media type
// (single image / album / video), edition tag, publish toggle. No blog complexity.
export const AdminFestivalNews = ({ value = [], editions = [], onChange }) => {
  const [open, setOpen] = useState(null);
  const items = value || [];
  const set = (arr) => onChange(arr);
  const patch = (i, p) => { const c = [...items]; c[i] = { ...c[i], ...p }; set(c); };
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; set(c); };
  const add = () => {
    set([{ id: uid(), title: "", date: "", cover: "", excerpt: "", content: "", mediaType: "image", gallery: [], videoUrl: "", edition: (editions[0]?.year || ""), status: "published", order: 0 }, ...items]);
    setOpen(0);
  };
  const years = [...new Set((editions || []).map((e) => e.year).filter(Boolean))];

  return (
    <div className="space-y-5" data-testid="admin-festival-news">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg text-white inline-flex items-center gap-2"><Newspaper size={18} className="text-electric" /> Actualités du Festival</h2>
          <p className="text-[11px] text-secondary2 mt-1">Cartes simples (couverture, date, titre). Une actualité peut contenir une image, un album photo ou une vidéo. Spécifique au Festival.</p>
        </div>
        <button onClick={add} data-testid="admin-news-add" className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-electric text-black text-[11px] tracking-[0.12em] uppercase font-semibold hover:brightness-110"><Plus size={15} /> Ajouter</button>
      </div>

      {open !== null && items[open] ? (
        <FicheOverlay testid="festivalnews-fiche" title={items[open].title || `Actualité #${open + 1}`} subtitle={`Festival${items[open].edition ? ` · Édition ${items[open].edition}` : ""}`} onClose={() => setOpen(null)}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2"><Input label="Titre" value={items[open].title} onChange={(v) => patch(open, { title: v })} placeholder="ex: Appel à projets 2026" /></div>
            <Input label="Date" value={items[open].date} onChange={(v) => patch(open, { date: v })} placeholder="ex: 12 juin 2026" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select label="Type de média" value={items[open].mediaType || "image"} onChange={(v) => patch(open, { mediaType: v })}
              options={[{ value: "image", label: "Image unique" }, { value: "album", label: "Album photos" }, { value: "video", label: "Vidéo" }]} />
            {years.length > 0
              ? <Select label="Édition" value={items[open].edition || ""} onChange={(v) => patch(open, { edition: v })} options={[{ value: "", label: "—" }, ...years.map((y) => ({ value: String(y), label: String(y) }))]} />
              : <Input label="Édition (année)" value={items[open].edition} onChange={(v) => patch(open, { edition: v })} placeholder="2026" />}
            <div className="flex items-end pb-1"><Toggle label="Publier (visible sur le site)" checked={items[open].status !== "hidden"} onChange={(v) => patch(open, { status: v ? "published" : "hidden" })} /></div>
          </div>

          <ImageInput label="Image de couverture" value={items[open].cover} onChange={(v) => patch(open, { cover: v })} />
          <Area label="Introduction (1-2 lignes, optionnel)" value={items[open].excerpt} onChange={(v) => patch(open, { excerpt: v })} rows={2} />
          <Area label="Contenu (optionnel)" value={items[open].content} onChange={(v) => patch(open, { content: v })} rows={4} aiContext="une actualité du Karams Festival" />

          {items[open].mediaType === "album" && (
            <GalleryField label="Album photos" items={items[open].gallery || []} onItemsChange={(v) => patch(open, { gallery: v })} testid={`admin-news-gallery-${open}`} />
          )}
          {items[open].mediaType === "video" && (
            <VideoInput label="Vidéo (MP4 ou URL YouTube/Vimeo)" value={items[open].videoUrl} onChange={(v) => patch(open, { videoUrl: v })} />
          )}
        </FicheOverlay>
      ) : (
        <>
          {items.length === 0 && <p className="text-secondary2 text-sm">Aucune actualité. Cliquez « Ajouter ».</p>}
          <CardGrid testid="festivalnews-cards">
            {items.map((n, i) => (
              <ItemCard key={n.id || i} testid={`admin-news-${i}`}
                image={n.cover} title={n.title || `Actualité #${i + 1}`} subtitle={[n.date, n.edition && `Édition ${n.edition}`].filter(Boolean).join(" · ")}
                dimmed={n.status === "hidden"}
                badges={[
                  { label: n.status === "hidden" ? "Masquée" : "Publiée", tone: n.status === "hidden" ? "text-white/50 border-white/15 bg-black/60" : "text-electric border-electric/40 bg-black/60" },
                  { label: n.mediaType === "album" ? "Album" : n.mediaType === "video" ? "Vidéo" : "Image", tone: "text-white/60 border-white/15 bg-black/60" },
                ]}
                onOpen={() => setOpen(i)}
                actions={
                  <>
                    <button onClick={() => move(i, -1)} title="Monter" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowUp size={14} /></button>
                    <button onClick={() => move(i, 1)} title="Descendre" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowDown size={14} /></button>
                    <button onClick={() => set(items.filter((_, x) => x !== i))} data-testid={`admin-news-delete-${i}`} title="Supprimer" className="p-2 rounded border border-white/10 text-red-400/70 hover:text-red-400 hover:border-red-400/40"><Trash2 size={14} /></button>
                  </>
                } />
            ))}
          </CardGrid>
        </>
      )}
    </div>
  );
};
